import { Shield, UserCog } from "lucide-react";
import type {
  AssetStatus,
  AssetType,
  DeploymentStatus,
  DossierStatus,
  RequestStatus,
  V2Role,
} from "@/v2/shared/model";
import type { VBadgeProps } from "@/components/ui";

type BadgeColor = NonNullable<VBadgeProps["color"]>;

type Meta<T extends string> = Record<T, { label: string; color: BadgeColor }>;

// Vaste kleurconventie voor statusbadges — betekenis bepaalt de kleur, niet de
// enum-naam. Deze module is de enige bron voor statuskleuren; hanteer dezelfde
// mapping voor nieuwe statussen zodat gelijkaardige gevallen altijd dezelfde
// kleur krijgen:
//   info (blauw)    → nog te behandelen / gepland (Open, Gepland)
//   success (groen) → actief/bevestigd/beschikbaar (Aanvaard, Ingepland, Geplaatst, Beschikbaar, In gebruik)
//   warning (amber) → wacht op actie / tijdelijk onbeschikbaar (Op wachtrij, In onderhoud)
//   error (rood)    → beëindigd of buiten dienst (Afgesloten, Buiten werking)
//   neutral (grijs) → afgeronde/neutrale eindstaat (Afgerond)
export const requestStatusMeta: Meta<RequestStatus> = {
  OPEN: { label: "Open", color: "info" },
  ACCEPTED: { label: "Aanvaard", color: "success" },
  CLOSED: { label: "Afgesloten", color: "error" },
};

export const dossierStatusMeta: Meta<DossierStatus> = {
  ON_WAITLIST: { label: "Op wachtrij", color: "warning" },
  PLANNED: { label: "Ingepland", color: "success" },
  CLOSED: { label: "Afgesloten", color: "error" },
};

export const deploymentStatusMeta: Meta<DeploymentStatus> = {
  PLANNED: { label: "Gepland", color: "info" },
  DEPLOYED: { label: "Geplaatst", color: "success" },
  // "Afgerond" is een positieve eindstaat (opgehaald), dus neutraal en niet rood.
  CLOSED: { label: "Afgerond", color: "neutral" },
};

export const assetStatusMeta: Meta<AssetStatus> = {
  AVAILABLE: { label: "Beschikbaar", color: "success" },
  IN_USE: { label: "In gebruik", color: "info" },
  OUT_OF_ORDER: { label: "Buiten werking", color: "error" },
  IN_MAINTENANCE: { label: "In onderhoud", color: "warning" },
};

export const assetTypeLabels: Record<AssetType, string> = {
  msp: "MSP",
  mobileTree: "Mobile tree",
  camcar: "Camcar",
  camtower: "Camtower",
};

export const roleLabels: Record<V2Role, string> = {
  requester: "Aanvrager",
  manager: "Beheerder",
};

export const roleIcons: Record<V2Role, React.ComponentType<{ className?: string }>> = {
  requester: UserCog,
  manager: Shield,
};
